import React, { memo, useMemo, useEffect, useState, useCallback } from 'react';
import classnames from 'classnames';
import {
    createUseStyles,
    Button,
    Text,
    Icon,
    Menus,
    Checkbox,
    Loader,
    useTheme,
} from '@Sage/hoi-poi-ui';
import { useWeb5Context } from 'lib/Web5Context';
import { saveDashboardFilters } from 'lib/services/forceBI';
import ModalConfirm from 'components/ModalConfirm';
import ModalSaveBeforeLeave from './ModalSaveBeforeLeave';
import { successToast, errorToast } from 'utils/toast';
import copyToClipboard from 'utils/copy';
import { CHARTS_LIST, FORCE_BI_SUBMODULE } from '../../constants';

import styles from './styles';
import useBlockNavigation from 'hooks/useBlockNavigation';

const useStyles = createUseStyles(styles, 'Header');

const Header = memo(
    ({
        dashboardId,
        onOpenCreate,
        filtersComponent,
        entity,
        filtersAddButtonSchema,
        onSelectAll,
        widgets,
        selected,
        onClearSelected,
        onDeleteMultiple,
        onOpenShare,
        onOpenBulkShare,
        onDownloadImages,
        onDownloadTables,
        canUseEmail,
        canShare,
        canCreate,
        canDelete,
        canSaveFilters,
        forceBIFiltersProps,
        forceBIUtils,
        hasWidgets,
        areFiltersSaved,
        setAreFiltersSaved,
        areFiltersChanged,
        isLoading,
        customDashboardFilters,
        hashHistory,
        ensureRoute,
        forceBIId,
        hiddenFilterIds = [],
    }) => {
        const theme = useTheme();
        const classes = useStyles();
        const { t, entities, getWeb4EntityFromString, logEvent } = useWeb5Context();
        const [innerSelected, setInnerSelected] = useState(selected || []);
        const [isBulkDeleteOpen, setIsBulkDeleteOpen] = useState(false);
        const [isLoadingEmailBulk, setIsLoadingEmailBulk] = useState(false);
        const [isLoadingImages, setIsLoadingImages] = useState(false);
        const [isLoadingTables, setIsLoadingTables] = useState(false);
        const [isLoadingSaveFilters, setIsLoadingSaveFilters] = useState(false);

        useEffect(() => {
            let timer;
            if (!selected.length) {
                timer = setTimeout(() => {
                    setInnerSelected([]);
                }, 300);
            } else {
                setInnerSelected(selected);
            }

            return () => timer && clearTimeout(timer);
        }, [selected]);

        const Filters = filtersComponent;

        const topFiltersProps = useMemo(
            () => ({
                className: classes.filters,
                useQuickFilters: true,
                entity,
                defaultFilters: [],
                defaultQuickFilters: [],
                ignoreDefaultCrossFilters: true,
                filtersAddButtonSchema,
                showQuickFiltersAdd: true,
                forceSelectAllTypes: true,
                useAddIcon: true,
                entityToForceLabel: entities.COMPANIES,
                hiddenFilters: hiddenFilterIds,
                ...forceBIFiltersProps,
            }),
            [
                entity,
                filtersAddButtonSchema,
                forceBIFiltersProps,
                classes?.filters,
                entities,
                hiddenFilterIds,
            ],
        );

        const onBulkDeleteOpen = useCallback(() => setIsBulkDeleteOpen(true), []);
        const onCancelBulkDelete = useCallback(() => setIsBulkDeleteOpen(false), []);
        const handleOnDeleteMultiple = useCallback(() => {
            onDeleteMultiple().then(() => {
                setIsBulkDeleteOpen(false);
            });
        }, [onDeleteMultiple]);

        const modalConfirmProps = useMemo(() => {
            return {
                onConfirm: handleOnDeleteMultiple,
                onCancel: onCancelBulkDelete,
                isOpen: isBulkDeleteOpen,
                title: t('title_delete_selected_widgets'),
                advice: t('label_delete_widgets_alert'),
                writeXToConfirm: 'label_type_confirmation',
                passPhrase: t('action_delete'),
                confirmText: t('action_delete'),
                isConfirmLoading: false,
                forceSelectAllTypes: true,
            };
        }, [isBulkDeleteOpen, handleOnDeleteMultiple, onCancelBulkDelete, t]);

        const isIndeterminate = useMemo(() => {
            if (selected.length > 0 && selected.length < widgets.length) return true;
            return false;
        }, [selected, widgets]);

        const isChecked = useMemo(() => {
            if ((selected.length > 0 && selected.length === widgets.length) || isIndeterminate)
                return true;
            return false;
        }, [selected, widgets, isIndeterminate]);

        const labelSelected = useMemo(() => {
            if (innerSelected?.length === 1) return t('label_num_widgets_selected_singular');
            return t('label_num_widgets_selected');
        }, [innerSelected, t]);

        const isSaveFiltersDisabled = useMemo(() => {
            const { customCache, filters, crossFilters, operators, crossOperators } =
                topFiltersProps;

            if (!customCache?.visibleFilters?.length && !areFiltersChanged) return true;

            const numberCrossFilters = Object.entries(crossFilters).reduce(
                (num, [entity, filters]) => {
                    if (Object.keys(filters).length) num++;
                    return num;
                },
                0,
            );
            const numberCrossOperators = Object.entries(crossOperators).reduce(
                (num, [entity, operators]) => {
                    if (Object.keys(operators).length) num++;
                    return num;
                },
                0,
            );

            if (areFiltersSaved) return true;
            if (areFiltersChanged) return false;
            if (
                !Object.keys(filters).length &&
                !Object.keys(operators).length &&
                !numberCrossFilters &&
                !numberCrossOperators
            )
                return true;
            return false;
        }, [topFiltersProps, areFiltersSaved, areFiltersChanged]);

        const onSaveFilters = useCallback(() => {
            if (!canSaveFilters || isSaveFiltersDisabled) return Promise.resolve();

            setIsLoadingSaveFilters(true);
            const { filters, crossFilters, operators, crossOperators, customCache } =
                topFiltersProps;
            const { translateFiltersForBackend } = forceBIUtils;
            const { filters: newFilters, operators: newOperators } = translateFiltersForBackend({
                filters,
                crossFilters,
                operators,
                crossOperators,
            });

            return saveDashboardFilters(dashboardId, {
                filters: newFilters,
                operators: newOperators,
                visibleFilters: customCache.visibleFilters,
            })
                .then(() => {
                    setAreFiltersSaved(true);
                    successToast({ t, text: t('label_save_filters_success') });
                })
                .catch(() => {
                    errorToast({ t, text: t('label_save_filters_error') });
                })
                .finally(() => {
                    setIsLoadingSaveFilters(false);
                });
        }, [
            canSaveFilters,
            topFiltersProps,
            forceBIUtils,
            dashboardId,
            t,
            isSaveFiltersDisabled,
            setAreFiltersSaved,
        ]);

        const shouldBlockNavigation = useMemo(() => {
            return canSaveFilters && !isSaveFiltersDisabled;
        }, [canSaveFilters, isSaveFiltersDisabled]);

        const { isBlockModalOpen, onConfirmAndUnblock, onCancelAndUnblock } = useBlockNavigation({
            hashHistory,
            ensureRoute,
            shouldBlockNavigation,
            confirmPromise: onSaveFilters,
        });

        const isBulkLoading = useMemo(() => {
            return isLoadingEmailBulk || isLoadingImages || isLoadingTables;
        }, [isLoadingEmailBulk, isLoadingImages, isLoadingTables]);

        const isLoadingImagesOrTables = useMemo(() => {
            return isLoadingImages || isLoadingTables;
        }, [isLoadingImages, isLoadingTables]);

        const handleDownloadImages = useCallback(() => {
            if (isBulkLoading) return;
            setIsLoadingImages(true);
            onDownloadImages().finally(() => {
                setIsLoadingImages(false);
            });
        }, [isBulkLoading, onDownloadImages]);

        const handleDownloadTables = useCallback(() => {
            if (isBulkLoading) return;
            setIsLoadingTables(true);
            onDownloadTables().finally(() => {
                setIsLoadingTables(false);
            });
        }, [isBulkLoading, onDownloadTables]);

        const downloadOptions = useMemo(() => {
            const options = [];

            const imagesToDownload = widgets.reduce((arr, current) => {
                if (
                    selected.includes(parseInt(current.id, 10)) &&
                    CHARTS_LIST.includes(current.chartType)
                )
                    arr.push(parseInt(current.id, 10));
                return arr;
            }, []);

            if (imagesToDownload.length) {
                options.push({
                    title: (
                        <Text type="body" color={theme.colors.utility.textPrimary}>
                            {t('label_download_as_image')}
                        </Text>
                    ),
                    icon: <Icon name="image" color={theme.colors.actionMinor[500]} />,
                    onClick: handleDownloadImages,
                });
            }

            options.push({
                title: (
                    <Text type="body" color={theme.colors.utility.textPrimary}>
                        {t('label_download_as_excel')}
                    </Text>
                ),
                icon: (
                    <Icon
                        className={classes.excelIcon}
                        name="xls"
                        color={theme.colors.actionMinor[500]}
                    />
                ),
                onClick: handleDownloadTables,
            });

            return options;
        }, [
            handleDownloadImages,
            handleDownloadTables,
            theme.colors,
            classes.excelIcon,
            t,
            selected,
            widgets,
        ]);

        const shareOptions = useMemo(() => {
            const getDashboardUrl = () => {
                if (!forceBIId) return window.location.href;

                const baseUrl = window.location.origin;
                const dashboardPath = `/#${entities.ANALYTICS_REPORT.route}/${forceBIId}`;
                return `${baseUrl}${dashboardPath}`;
            };

            return [
                {
                    title: (
                        <Text type="body" color={theme.colors.utility.textPrimary}>
                            {t('action_copy')}
                        </Text>
                    ),
                    icon: <Icon name="contentLink" color={theme.colors.actionMinor[500]} />,
                    onClick: () => {
                        const dashboardUrl = getDashboardUrl();
                        copyToClipboard(dashboardUrl, t);
                        logEvent({
                            event: entities.ANALYTICS_REPORT.trueName,
                            functionality: 'share',
                            submodule: FORCE_BI_SUBMODULE,
                        });
                    },
                },
                {
                    title: (
                        <Text type="body" color={theme.colors.utility.textPrimary}>
                            {t('label_share_dashboard')}
                        </Text>
                    ),
                    icon: <Icon name="email" color={theme.colors.actionMinor[500]} />,
                    onClick: () => onOpenShare(getDashboardUrl()),
                },
            ];
        }, [onOpenShare, t, theme.colors, logEvent, entities, forceBIId]);

        const labelSelectUnselect = useMemo(() => {
            if (selected.length) return t('label_clear_selection');
            return t('label_selectall');
        }, [selected, t]);

        const handleOnOpenBulkShare = useCallback(() => {
            setIsLoadingEmailBulk(true);
            onOpenBulkShare().finally(() => {
                setIsLoadingEmailBulk(false);
            });
        }, [onOpenBulkShare]);

        const hasCustomDashboardFilters = useMemo(
            () => customDashboardFilters && Object.keys(customDashboardFilters).length,
            [customDashboardFilters],
        );

        const renderCustomFilters = useMemo(() => {
            if (!customDashboardFilters || !Object.keys(customDashboardFilters).length) return null;
            return Object.entries(customDashboardFilters).reduce((arr, [entity, filters]) => {
                if (!filters || !Object.entries(filters)) return arr;
                Object.entries(filters).forEach(([id, filter]) => {
                    const newEntity = getWeb4EntityFromString(entity);
                    arr.push(
                        <div key={id} className={classes.customFiltersContainer}>
                            <div className={classes.customFilter}>
                                <Text
                                    color={theme.colors.semantic.info500}
                                >{`${filter.completeValues.label} (${t(newEntity.labels.plural)})`}</Text>
                            </div>
                        </div>,
                    );
                });
                return arr;
            }, []);
        }, [
            customDashboardFilters,
            classes.customFilter,
            classes.customFiltersContainer,
            getWeb4EntityFromString,
            t,
            theme,
        ]);

        return (
            <div className={classes.root}>
                {widgets.length > 0 && (
                    <div className={classes.left}>
                        {widgets?.length > 0 && (
                            <div className={classes.selectAll}>
                                <Checkbox
                                    color="actionMajor"
                                    onChange={onSelectAll}
                                    checked={isChecked}
                                    indeterminate={isIndeterminate}
                                />
                                <Text>{labelSelectUnselect}</Text>
                            </div>
                        )}
                        {selected?.length > 0 && (
                            <div className={classes.bulkActions}>
                                <div className={classes.separator} />
                                <Text>{`${innerSelected.length} ${labelSelected}`}</Text>
                                <div className={classes.actions}>
                                    {canDelete && onDeleteMultiple && (
                                        <Icon
                                            name="delete"
                                            onClick={onBulkDeleteOpen}
                                            tooltip={t('label_delete_selected')}
                                        />
                                    )}
                                    {!isLoadingImagesOrTables && (
                                        <Menus
                                            className={classes.downloadMenu}
                                            classNameMenu={classes.downloadMenuPopover}
                                            title={
                                                <Icon
                                                    name="fileDownload"
                                                    tooltip={t('label_download_selected')}
                                                />
                                            }
                                            items={downloadOptions}
                                            size="large"
                                            triggerAction="hover"
                                        />
                                    )}
                                    {isLoadingImagesOrTables && <Loader size="tiny" />}
                                    {canShare && !isLoadingEmailBulk && (
                                        <Icon
                                            name="email"
                                            onClick={handleOnOpenBulkShare}
                                            tooltip={t('label_share_selected')}
                                        />
                                    )}
                                    {canShare && isLoadingEmailBulk && <Loader size="tiny" />}
                                </div>
                            </div>
                        )}
                    </div>
                )}
                {selected?.length === 0 && (
                    <>
                        <div className={classes.center}>
                            {widgets.length > 0 &&
                                (forceBIFiltersProps?.customCache?.visibleFilters?.length > 0 ||
                                    hasCustomDashboardFilters) &&
                                !isLoading && <div className={classes.dividerFilters} />}
                            {!isLoading && renderCustomFilters}
                            {hasWidgets && <Filters {...topFiltersProps} />}
                        </div>
                        <div className={classes.right}>
                            {hasWidgets && canSaveFilters && (
                                <div
                                    className={classnames(classes.saveButton, {
                                        [classes.saveButtonDisabled]: isSaveFiltersDisabled,
                                        [classes.saveButtonLoading]: isLoadingSaveFilters,
                                    })}
                                >
                                    {isLoadingSaveFilters && <Loader size="tiny" />}
                                    {!isLoadingSaveFilters && (
                                        <Icon
                                            name="save"
                                            size="large"
                                            tooltip={t('action_save_filters')}
                                            onClick={!isSaveFiltersDisabled ? onSaveFilters : null}
                                        />
                                    )}
                                </div>
                            )}
                            {widgets?.length > 0 && (canShare || canCreate) && (
                                <div className={classes.dividerActions} />
                            )}
                            {hasWidgets && canUseEmail && canShare && (
                                <Menus
                                    className={classes.shareMenu}
                                    classNameMenu={classes.shareMenuPopover}
                                    title={
                                        <Button type="terciary" icon="share">
                                            {t('action_share')}
                                        </Button>
                                    }
                                    items={shareOptions}
                                    size="medium"
                                />
                            )}
                            {canCreate && (
                                <Button onClick={onOpenCreate} isDisabled={isLoading}>
                                    {t('label_forcebi_crud_title_placeholder')}
                                </Button>
                            )}
                        </div>
                    </>
                )}
                <ModalConfirm {...modalConfirmProps} />
                <ModalSaveBeforeLeave
                    isOpen={isBlockModalOpen}
                    onCancel={onCancelAndUnblock}
                    onConfirm={onConfirmAndUnblock}
                />
            </div>
        );
    },
);

export default Header;
